import { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

const newsItems = [
  {
    title: "Quantum Computing Advances Push Encryption to Its Limits",
    description: "The impending quantum computer revolution, known as Q-Day, threatens to undermine global encryption, exposing sensitive data and critical systems.",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=600&q=60",
    link: "https://www.wired.com/story/q-day-apocalypse-quantum-computers-encryption",
    date: "2025-03-25"
  },
  {
    title: "UK Cybersecurity Agency Warns Over Risk of Quantum Hackers",
    description: "The UK's National Cyber Security Centre (NCSC) advises organizations to secure systems against potential quantum computing threats by 2035.",
    image: "https://images.unsplash.com/photo-1517373116369-9bdb8cdc9f62?w=600&q=60",
    link: "https://www.theguardian.com/technology/2025/mar/20/uk-cybersecurity-agency-quantum-hackers",
    date: "2025-03-20"
  },
  {
    title: "A Looming Threat to Bitcoin: The Risk of a Quantum Hack",
    description: "Bitcoin faces a significant potential risk from quantum computing, which could ultimately break the encryption that secures digital assets.",
    image: "https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=600&q=60",
    link: "https://www.wsj.com/tech/cybersecurity/a-looming-threat-to-bitcoin-the-risk-of-a-quantum-hack-24637e29",
    date: "2025-01-15"
  },
  {
    title: "Quantum Chips Pose Huge Threat to Data Encryption",
    description: "The advent of quantum computing, marked by Google's release of the Willow chip, poses a significant threat to current data encryption methods.",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&q=60",
    link: "https://www.theaustralian.com.au/business/qanapi-boss-trent-telford-says-quantum-computing-could-blow-apart-current-data-encryption/news-story/c9e2b014fefb298d902f7516744d39fb",
    date: "2025-01-10"
  },
  {
    title: "'Q Day' Is Coming. It's Time to Worry About Quantum Security.",
    description: "Experts warn that quantum computers could break existing encryption by 2035, posing significant risks to data security.",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&q=60",
    link: "https://www.wsj.com/articles/q-day-is-coming-its-time-to-worry-about-quantum-security-ca88c576",
    date: "2024-10-05"
  },
  {
    title: "Europol Body: Banks Should Prepare for Quantum Computer Risk Now",
    description: "A Europol-affiliated group recommends that the financial sector begin preparations for the risks posed by quantum computers, which could break encryption used to protect sensitive data.",
    image: "https://quantumzeitgeist.com/wp-content/uploads/padlock2-1.png.webp",
    link: "https://www.reuters.com/technology/cybersecurity/europol-body-banks-should-prepare-quantum-computer-risk-now-2025-02-07/",
    date: "2025-02-07"
  },
  {
    title: "Cybersecurity Experts Urge Immediate Action Against Quantum Threats",
    description: "As quantum computing capabilities advance, cybersecurity professionals emphasize the urgent need to transition to quantum-resistant encryption methods.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=60",
    link: "https://www.securityweek.com/cyber-insights-2025-quantum-and-the-threat-to-encryption/",
    date: "2025-02-28"
  },
  {
    title: "Financial Institutions Face Quantum Computing Security Challenges",
    description: "The financial sector is advised to assess and mitigate risks associated with quantum computing, which could compromise current cryptographic protocols.",
    image: "https://images.unsplash.com/photo-1580519542036-c47de6196ba5?w=600&q=60",
    link: "https://industrialcyber.co/reports/moodys-sounds-alarm-on-quantum-computing-risk-as-transition-to-pqc-will-be-long-and-costly/",
    date: "2024-08-15"
  },
  {
    title: "Quantum Computing Threatens Blockchain Integrity",
    description: "Advancements in quantum technology pose a risk to blockchain security, potentially undermining the integrity of decentralized systems.",
    image: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=600&q=60",
    link: "https://www.theverge.com/tech/633248/beyond-the-hype-of-quantum-computers",
    date: "2025-03-22"
  },
  {
    title: "Preparing for the Quantum Era: Steps to Secure Data Now",
    description: "Organizations are encouraged to adopt quantum-resistant encryption strategies to safeguard sensitive information against future quantum attacks.",
    image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=600&q=60",
    link: "https://www.secureworld.io/industry-news/quantum-threats-protect-your-data",
    date: "2025-01-30"
  }
];




export default function QuantumThreats() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  return (
    <div className="py-0 md:py-0 w-full relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="font-tokyo text-3xl font-bold bg-gradient-to-r from-quantum-primary to-quantum-secondary bg-clip-text text-transparent mb-2">
            Quantum Threats Monitor
          </h2>
          <p className="text-sm text-quantum-light/80">
            Stay informed about the latest quantum computing developments threatening blockchain security
          </p>
        </div>

        <div className="relative">
          {/* Left Arrow */}
          {showLeftArrow && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 quantum-card p-2 hover:bg-white/10"
            >
              <ChevronLeft className="w-6 h-6 text-quantum-primary" />
            </button>
          )}

          {/* Right Arrow */}
          {showRightArrow && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 quantum-card p-2 hover:bg-white/10"
            >
              <ChevronRight className="w-6 h-6 text-quantum-primary" />
            </button>
          )}

          {/* News Container */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto gap-4 pb-4 hide-scrollbar"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {newsItems.map((item, index) => (
              <a
                key={index}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="quantum-card flex-none w-[300px] md:w-[400px] group hover:border-quantum-primary/30 transition-all duration-300"
              >
                <div className="relative h-[200px] overflow-hidden rounded-t-xl">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-quantum-dark to-transparent" />
                  <div className="absolute bottom-2 left-3 text-xs text-quantum-light/70">{item.date}</div>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-quantum-light mb-2 flex items-center gap-2">
                    {item.title}
                    <ExternalLink className="w-4 h-4 text-quantum-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </h3>
                  <p className="text-sm text-quantum-light/70">{item.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}